import { AiBadgeIconGradient, IconName } from '@ardoq/icons';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { showViewSidePanel } from 'views/mainApp/viewSidePanel/actions';
import { ViewSidePanelContentIds } from 'views/mainApp/viewSidePanel/types';
import {
  ViewOptionsSectionBody,
  ViewOptionsSectionContainer,
  ViewOptionsSectionHeader,
  ViewOptionsSectionTitle,
  ViewOptionsSectionTitleContainer,
} from './atoms';
import { perspectivesEditor$ } from 'perspectiveSidebar/perspectiveEditor/perspectivesEditor$';
import { map } from 'rxjs/operators';
import type { ExtractStreamShape } from 'tabview/types';
import { calculateRulesCountPerTab } from 'perspectiveSidebar/perspectiveEditor/perspectivesEditor$/calculateRulesCountPerTab';
import { ViewOptionsSectionRow } from './ViewOptionsSectionRow';
import { usePerspectiveEditorPortalSubscription } from 'perspectiveSidebar/perspectiveEditor/perspectivesEditor$/usePerspectiveEditorPortalSubscription';
import { trackViewOptionClick } from '../tracking';
import { combineLatest } from 'rxjs';
import { featureContext$ } from 'streams/featureContext$';
import { filterValidConditionalFormattingRules } from 'perspectiveSidebar/perspectiveEditor/formattingUtils';
import { featureOperations, Features } from '@ardoq/features';
import { openSidebarPanel } from 'canvas/sidebarPanel/actions';
import { SidebarPanelContentIds } from 'canvas/sidebarPanel/types';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t } from '@ardoq/i18n';
import { isPerspectivesAiButtonVisible$ } from 'perspectiveSidebar/perspectiveEditor/isPerspectivesAiButtonVisible$';

type ViewOptionsPerspectivesProperties = ExtractStreamShape<
  typeof viewOptionsPerspectives$
>;
const ViewOptionsPerspectivesBase = ({
  groupingRulesCount,
  formattingRulesCount,
  labelRulesCount,
  handleGroupsRowClick,
  handleConditionalFormatRowClick,
  handleLabelsRowClick,
  isAiButtonVisible,
  i18nLocale,
}: ViewOptionsPerspectivesProperties) => {
  usePerspectiveEditorPortalSubscription();
  const aiBadge = isAiButtonVisible ? <AiBadgeIconGradient /> : undefined;
  return (
    <ViewOptionsSectionContainer>
      <ViewOptionsSectionHeader>
        <ViewOptionsSectionTitleContainer>
          <ViewOptionsSectionTitle>
            {t(i18nLocale, 'appLayout:viewOptionsTitle', 'View options')}
          </ViewOptionsSectionTitle>
        </ViewOptionsSectionTitleContainer>
      </ViewOptionsSectionHeader>
      <ViewOptionsSectionBody>
        <ViewOptionsSectionRow
          rowLabel={t(i18nLocale, 'appLayout:groupsRowLabel', 'Groups')}
          iconName={IconName.APPLICATION_GROUP}
          tagContent={groupingRulesCount}
          onClick={handleGroupsRowClick}
        />
        <ViewOptionsSectionRow
          rowLabel={t(
            i18nLocale,
            'appLayout:conditionalFormatRowLabel',
            'Conditional format'
          )}
          iconName={IconName.FORMAT_LIST_BULLETED}
          tagContent={formattingRulesCount}
          onClick={handleConditionalFormatRowClick}
          badge={aiBadge}
        />
        <ViewOptionsSectionRow
          rowLabel={t(i18nLocale, 'appLayout:labelsRowLabel', 'Labels')}
          iconName={IconName.LABEL_OUTLINED}
          tagContent={labelRulesCount}
          onClick={handleLabelsRowClick}
          badge={aiBadge}
        />
      </ViewOptionsSectionBody>
    </ViewOptionsSectionContainer>
  );
};

const viewOptionsPerspectives$ = combineLatest({
  state: perspectivesEditor$,
  featureContext: featureContext$,
  i18nLocale: i18nLocale$,
  isAiButtonVisible: isPerspectivesAiButtonVisible$,
}).pipe(
  map(({ state, featureContext, i18nLocale, isAiButtonVisible }) => {
    const { GROUPS: groupingRulesCount, LABELS: labelRulesCount } =
      calculateRulesCountPerTab(state);

    const { formattingTab, perspectivesRelatedData, datasetRelatedData } =
      state;

    const { conditionalFormattingRules: rawConditionalFormattingRules } =
      formattingTab;

    const { referenceTypeNames, componentTypeNames } = perspectivesRelatedData;

    const componentTypeNamesSet = datasetRelatedData
      ? datasetRelatedData.componentTypeNames
      : new Set(componentTypeNames);

    const referenceTypeNamesSet = datasetRelatedData
      ? datasetRelatedData.referenceTypeNames
      : new Set(referenceTypeNames);

    const conditionalFormattingRules = filterValidConditionalFormattingRules(
      rawConditionalFormattingRules,
      { componentTypeNamesSet, referenceTypeNamesSet }
    );

    const formattingRulesCount = conditionalFormattingRules.length;

    // KFQ12026
    const isCanvasPrototype = featureOperations.hasFeature(
      featureContext,
      Features.CANVAS_PROTOTYPE
    );

    const handleGroupsRowClick = () => {
      trackViewOptionClick('group');
      if (isCanvasPrototype) {
        dispatchAction(
          openSidebarPanel(
            SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_GROUPS
          )
        );
      } else {
        dispatchAction(
          showViewSidePanel(ViewSidePanelContentIds.PERSPECTIVES_GROUPS)
        );
      }
    };

    const handleConditionalFormatRowClick = () => {
      trackViewOptionClick('conditional formatting');
      if (isCanvasPrototype) {
        dispatchAction(
          openSidebarPanel(
            SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT
          )
        );
      } else {
        dispatchAction(
          showViewSidePanel(
            ViewSidePanelContentIds.PERSPECTIVES_CONDITIONAL_FORMAT
          )
        );
      }
    };

    const handleLabelsRowClick = () => {
      trackViewOptionClick('label');
      if (isCanvasPrototype) {
        dispatchAction(
          openSidebarPanel(
            SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_LABELS
          )
        );
      } else {
        dispatchAction(
          showViewSidePanel(ViewSidePanelContentIds.PERSPECTIVES_LABELS)
        );
      }
    };

    return {
      groupingRulesCount,
      formattingRulesCount,
      labelRulesCount,
      handleGroupsRowClick,
      handleConditionalFormatRowClick,
      handleLabelsRowClick,
      isAiButtonVisible,
      i18nLocale,
    };
  })
);

export const ViewOptionsPerspectives = connect(
  ViewOptionsPerspectivesBase,
  viewOptionsPerspectives$
);
